import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const Lounge = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('chat');

  const sections = [
    { id: 'chat', label: '💬 Chat Rooms' },
    { id: 'forum', label: '📌 Discussion Forum' },
    { id: 'study', label: '📹 Open Study Room' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          <button style={styles.returnBtn} onClick={() => navigate('/campus')}>
            ← Return to Campus Square
          </button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>🛋️ The Lounge</h2>
          <p style={styles.pageSubtitle}>
            Chat, discuss, and study with your fellow students
          </p>
        </div>

        <div style={styles.tabs}>
          {sections.map(section => (
            <button
              key={section.id}
              style={activeSection === section.id ? styles.tabActive : styles.tab}
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </div>

        <div style={styles.content}>
          {activeSection === 'chat' && <ChatRooms user={user} />}
          {activeSection === 'forum' && <DiscussionForum user={user} />}
          {activeSection === 'study' && <StudyRoom user={user} />}
        </div>
      </div>
    </div>
  );
};

// ==================== CHAT ROOMS ====================
const ChatRooms = ({ user }) => {
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [roomForm, setRoomForm] = useState({ name: '', description: '' });
  const messagesEndRef = useRef(null);
  const intervalRef = useRef(null);
  const isAdmin = user?.role_id <= 3;

  useEffect(() => {
    fetchRooms();
  }, []);

  useEffect(() => {
    if (activeRoom) {
      fetchMessages(activeRoom.id);
      intervalRef.current = setInterval(() => {
        fetchMessages(activeRoom.id);
      }, 4000);
    }
    return () => clearInterval(intervalRef.current);
  }, [activeRoom]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lounge/rooms`);
      setRooms(res.data.rooms);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const res = await axios.get(`${API_URL}/api/lounge/rooms/${roomId}/messages`);
      setMessages(res.data.messages);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    try {
      await axios.post(`${API_URL}/api/lounge/rooms/${activeRoom.id}/messages`, {
        body: newMessage
      });
      setNewMessage('');
      fetchMessages(activeRoom.id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API_URL}/api/lounge/messages/${messageId}`);
      fetchMessages(activeRoom.id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateRoom = async () => {
    if (!roomForm.name) return;
    try {
      await axios.post(`${API_URL}/api/lounge/rooms`, roomForm);
      setRoomForm({ name: '', description: '' });
      setShowRoomForm(false);
      fetchRooms();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Delete this room and all its messages?')) return;
    try {
      await axios.delete(`${API_URL}/api/lounge/rooms/${roomId}`);
      if (activeRoom?.id === roomId) setActiveRoom(null);
      fetchRooms();
    } catch (err) {
      console.error(err);
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div style={styles.chatLayout}>
      {/* Rooms List */}
      <div style={styles.roomsList}>
        <div style={styles.roomsHeader}>
          <h3 style={styles.roomsTitle}>Rooms</h3>
          {isAdmin && (
            <button
              style={styles.addRoomBtn}
              onClick={() => setShowRoomForm(!showRoomForm)}
            >
              +
            </button>
          )}
        </div>

        {showRoomForm && (
          <div style={styles.roomForm}>
            <input
              style={styles.roomInput}
              placeholder="Room name"
              value={roomForm.name}
              onChange={e => setRoomForm({...roomForm, name: e.target.value})}
            />
            <input
              style={styles.roomInput}
              placeholder="Description (optional)"
              value={roomForm.description}
              onChange={e => setRoomForm({...roomForm, description: e.target.value})}
            />
            <button style={styles.createRoomBtn} onClick={handleCreateRoom}>
              Create
            </button>
          </div>
        )}

        {rooms.length === 0 && (
          <p style={styles.emptyRooms}>No rooms yet.</p>
        )}

        {rooms.map(room => (
          <div
            key={room.id}
            style={activeRoom?.id === room.id ? styles.roomItemActive : styles.roomItem}
            onClick={() => setActiveRoom(room)}
          >
            <div style={styles.roomItemLeft}>
              <p style={styles.roomName}># {room.name}</p>
              {room.last_message && (
                <p style={styles.roomPreview}>{room.last_message}</p>
              )}
            </div>
            {isAdmin && (
              <button
                style={styles.deleteRoomBtn}
                onClick={e => { e.stopPropagation(); handleDeleteRoom(room.id); }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Chat Window */}
      <div style={styles.chatWindow}>
        {!activeRoom && (
          <div style={styles.noRoom}>
            <p style={styles.noRoomText}>Select a room to start chatting</p>
          </div>
        )}

        {activeRoom && (
          <>
            <div style={styles.chatHeader}>
              <h4 style={styles.chatRoomName}># {activeRoom.name}</h4>
              {activeRoom.description && (
                <p style={styles.chatRoomDesc}>{activeRoom.description}</p>
              )}
            </div>

            <div style={styles.messagesFeed}>
              {messages.length === 0 && (
                <p style={styles.emptyChat}>
                  No messages yet. Say hello! 👋
                </p>
              )}
              {messages.map(msg => (
                <div key={msg.id} style={styles.chatMessage}>
                  <div style={styles.chatAvatar}>
                    {msg.first_name?.[0]}{msg.last_name?.[0]}
                  </div>
                  <div style={styles.chatMessageContent}>
                    <div style={styles.chatMessageHeader}>
                      <span style={styles.chatMessageAuthor}>
                        {msg.first_name} {msg.last_name}
                      </span>
                      <span style={styles.chatMessageTime}>
                        {timeAgo(msg.created_at)}
                      </span>
                    </div>
                    <p style={styles.chatMessageBody}>{msg.body}</p>
                  </div>
                  {(user?.role_id === 1 || msg.user_id === user?.id) && (
                    <button
                      style={styles.deleteMsgBtn}
                      onClick={() => handleDeleteMessage(msg.id)}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div style={styles.chatInputArea}>
              <input
                style={styles.chatInput}
                placeholder={`Message #${activeRoom.name}...`}
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
              />
              <button style={styles.chatSendBtn} onClick={handleSend}>
                Send
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
// ==================== DISCUSSION FORUM ====================
const DiscussionForum = ({ user }) => {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [threadDetail, setThreadDetail] = useState(null);
  const [showThreadForm, setShowThreadForm] = useState(false);
  const [threadForm, setThreadForm] = useState({ title: '', body: '' });
  const [newReply, setNewReply] = useState('');
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role_id <= 3;

  useEffect(() => { fetchThreads(); }, []);

  useEffect(() => {
    if (activeThread) fetchReplies(activeThread.id);
  }, [activeThread]);

  const fetchThreads = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lounge/threads`);
      setThreads(res.data.threads);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async (threadId) => {
    try {
      const res = await axios.get(`${API_URL}/api/lounge/threads/${threadId}/replies`);
      setThreadDetail(res.data.thread);
      setReplies(res.data.replies);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateThread = async () => {
    if (!threadForm.title || !threadForm.body) return;
    try {
      await axios.post(`${API_URL}/api/lounge/threads`, threadForm);
      setThreadForm({ title: '', body: '' });
      setShowThreadForm(false);
      fetchThreads();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm('Delete this thread?')) return;
    try {
      await axios.delete(`${API_URL}/api/lounge/threads/${threadId}`);
      if (activeThread?.id === threadId) setActiveThread(null);
      fetchThreads();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePinThread = async (threadId) => {
    try {
      await axios.patch(`${API_URL}/api/lounge/threads/${threadId}/pin`);
      fetchThreads();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReply = async () => {
    if (!newReply.trim()) return;
    try {
      await axios.post(
        `${API_URL}/api/lounge/threads/${activeThread.id}/replies`,
        { body: newReply }
      );
      setNewReply('');
      fetchReplies(activeThread.id);
      fetchThreads();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteReply = async (replyId) => {
    try {
      await axios.delete(`${API_URL}/api/lounge/replies/${replyId}`);
      fetchReplies(activeThread.id);
      fetchThreads();
    } catch (err) {
      console.error(err);
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div style={styles.forumLayout}>
      {/* Thread List */}
      <div style={styles.threadList}>
        <div style={styles.threadListHeader}>
          <h3 style={styles.threadListTitle}>Discussions</h3>
          <button
            style={styles.newThreadBtn}
            onClick={() => setShowThreadForm(!showThreadForm)}
          >
            + New Thread
          </button>
        </div>

        {showThreadForm && (
          <div style={styles.threadForm}>
            <input
              style={styles.input}
              placeholder="Thread title"
              value={threadForm.title}
              onChange={e => setThreadForm({...threadForm, title: e.target.value})}
            />
            <textarea
              style={styles.textarea}
              placeholder="What's on your mind?"
              value={threadForm.body}
              onChange={e => setThreadForm({...threadForm, body: e.target.value})}
              rows={3}
            />
            <div style={styles.formActions}>
              <button style={styles.saveBtn} onClick={handleCreateThread}>
                Post Thread
              </button>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowThreadForm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && <p style={styles.loadingText}>Loading...</p>}
        {!loading && threads.length === 0 && (
          <p style={styles.emptyText}>No threads yet. Start the conversation!</p>
        )}

        {threads.map(thread => (
          <div
            key={thread.id}
            style={activeThread?.id === thread.id ?
              styles.threadItemActive : styles.threadItem}
            onClick={() => setActiveThread(thread)}
          >
            <div style={styles.threadItemTop}>
              <p style={styles.threadItemTitle}>
                {thread.is_pinned && <span style={styles.pinIcon}>📌 </span>}
                {thread.title}
              </p>
              <div style={styles.threadItemActions}>
                {isAdmin && (
                  <>
                    <button
                      style={styles.pinBtn}
                      onClick={e => { e.stopPropagation(); handlePinThread(thread.id); }}
                    >
                      {thread.is_pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button
                      style={styles.deleteThreadBtn}
                      onClick={e => { e.stopPropagation(); handleDeleteThread(thread.id); }}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            </div>
            <p style={styles.threadItemMeta}>
              {thread.first_name} {thread.last_name} · {timeAgo(thread.created_at)} · {thread.reply_count} replies
            </p>
          </div>
        ))}
      </div>

      {/* Thread Detail */}
      <div style={styles.threadDetail}>
        {!activeThread && (
          <div style={styles.noRoom}>
            <p style={styles.noRoomText}>Select a thread to read and reply</p>
          </div>
        )}

        {activeThread && threadDetail && (
          <>
            <div style={styles.threadDetailHeader}>
              <h3 style={styles.threadDetailTitle}>{threadDetail.title}</h3>
              <p style={styles.threadDetailMeta}>
                {threadDetail.first_name} {threadDetail.last_name} · {timeAgo(threadDetail.created_at)}
              </p>
              <p style={styles.threadDetailBody}>{threadDetail.body}</p>
            </div>

            <div style={styles.repliesList}>
              {replies.length === 0 && (
                <p style={styles.emptyText}>No replies yet. Be the first!</p>
              )}
              {replies.map(reply => (
                <div key={reply.id} style={styles.replyCard}>
                  <div style={styles.replyHeader}>
                    <div style={styles.replyAvatar}>
                      {reply.first_name?.[0]}{reply.last_name?.[0]}
                    </div>
                    <div style={styles.replyMeta}>
                      <p style={styles.replyAuthor}>
                        {reply.first_name} {reply.last_name}
                      </p>
                      <p style={styles.replyTime}>{timeAgo(reply.created_at)}</p>
                    </div>
                    {(user?.role_id === 1 || reply.user_id === user?.id) && (
                      <button
                        style={styles.deleteReplyBtn}
                        onClick={() => handleDeleteReply(reply.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <p style={styles.replyBody}>{reply.body}</p>
                </div>
              ))}
            </div>

            <div style={styles.replyInputArea}>
              <textarea
                style={styles.replyInput}
                placeholder="Write a reply..."
                value={newReply}
                onChange={e => setNewReply(e.target.value)}
                rows={3}
              />
              <button style={styles.replySendBtn} onClick={handleReply}>
                Post Reply
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== STUDY ROOM ====================
const StudyRoom = ({ user }) => {
  const [studyRoom, setStudyRoom] = useState(null);
  const [editForm, setEditForm] = useState({
    zoom_link: '', is_live: false, message: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [saved, setSaved] = useState(false);
  const isAdmin = user?.role_id <= 3;

  useEffect(() => { fetchStudyRoom(); }, []);

  const fetchStudyRoom = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lounge/study-room`);
      setStudyRoom(res.data.studyRoom);
      setEditForm({
        zoom_link: res.data.studyRoom.zoom_link || '',
        is_live: res.data.studyRoom.is_live || false,
        message: res.data.studyRoom.message || '',
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.patch(`${API_URL}/api/lounge/study-room`, editForm);
      setSaved(true);
      setEditMode(false);
      fetchStudyRoom();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={styles.studyRoomContainer}>
      <div style={styles.studyRoomCard}>
        <div style={styles.studyRoomHeader}>
          <div>
            <h3 style={styles.studyRoomTitle}>📹 Open Study Room</h3>
            <p style={styles.studyRoomSubtitle}>
              Join your fellow students for open study sessions
            </p>
          </div>
          {isAdmin && (
            <button
              style={styles.editStudyBtn}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? 'Cancel' : 'Edit'}
            </button>
          )}
        </div>

        {saved && <p style={styles.successText}>Study room updated!</p>}

        {editMode && isAdmin && (
          <div style={styles.studyEditForm}>
            <div style={styles.liveToggle}>
              <label style={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={editForm.is_live}
                  onChange={e => setEditForm({...editForm, is_live: e.target.checked})}
                  style={styles.toggleInput}
                />
                <span style={styles.toggleText}>Session is Live</span>
              </label>
            </div>
            <input
              style={styles.input}
              placeholder="Zoom link (e.g. https://zoom.us/j/...)"
              value={editForm.zoom_link}
              onChange={e => setEditForm({...editForm, zoom_link: e.target.value})}
            />
            <textarea
              style={styles.textarea}
              placeholder="Message to students (schedule, topic, etc.)"
              value={editForm.message}
              onChange={e => setEditForm({...editForm, message: e.target.value})}
              rows={3}
            />
            <button style={styles.saveBtn} onClick={handleSave}>
              Save Changes
            </button>
          </div>
        )}

        {studyRoom && !editMode && (
          <div style={styles.studyRoomBody}>
            <div style={styles.liveStatus}>
              <div style={{
                ...styles.liveDot,
                backgroundColor: studyRoom.is_live ? '#1E8449' : '#ffffff30',
                boxShadow: studyRoom.is_live ? '0 0 8px #1E8449' : 'none',
              }} />
              <span style={{
                ...styles.liveText,
                color: studyRoom.is_live ? '#1E8449' : '#ffffff50',
              }}>
                {studyRoom.is_live ? 'Session Live Now' : 'No Active Session'}
              </span>
            </div>

            {studyRoom.message && (
              <div style={styles.studyMessage}>
                <p style={styles.studyMessageText}>{studyRoom.message}</p>
              </div>
            )}

            {studyRoom.is_live && studyRoom.zoom_link && (
              <a
                href={studyRoom.zoom_link}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.joinBtn}
              >
                🎥 Join Study Session
              </a>
            )}

            {!studyRoom.is_live && (
              <p style={styles.noSessionText}>
                Check back later for the next scheduled session.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  header: { backgroundColor: '#0f1a33', borderBottom: '1px solid #C49A28', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  welcome: { color: '#ffffff', fontSize: '14px' },
  returnBtn: { backgroundColor: 'transparent', border: '1px solid #C49A28', color: '#C49A28', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '32px' },
  pageHeader: { marginBottom: '24px' },
  pageTitle: { color: '#C49A28', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  pageSubtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #ffffff15' },
  tab: { backgroundColor: 'transparent', border: 'none', color: '#ffffff60', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid transparent' },
  tabActive: { backgroundColor: 'transparent', border: 'none', color: '#C49A28', padding: '12px 20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', borderBottom: '2px solid #C49A28' },
  content: { minHeight: '400px' },
  chatLayout: { display: 'grid', gridTemplateColumns: '220px 1fr', gap: '0', height: '600px' },
  roomsList: { backgroundColor: '#0f1a33', borderRadius: '10px 0 0 10px', border: '1px solid #ffffff15', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  roomsHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #ffffff10' },
  roomsTitle: { color: '#ffffff80', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', margin: '0' },
  addRoomBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '4px', width: '24px', height: '24px', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' },
  roomForm: { padding: '12px', borderBottom: '1px solid #ffffff10', display: 'flex', flexDirection: 'column', gap: '6px' },
  roomInput: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '4px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  createRoomBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '4px', padding: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  emptyRooms: { color: '#ffffff30', fontSize: '12px', padding: '16px', margin: '0' },
  roomItem: { padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid #ffffff06', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  roomItemActive: { padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid #ffffff06', display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#1A2744' },
  roomItemLeft: { flex: 1, overflow: 'hidden' },
  roomName: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0 0 2px 0' },
  roomPreview: { color: '#ffffff40', fontSize: '11px', margin: '0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  deleteRoomBtn: { backgroundColor: 'transparent', border: 'none', color: '#ffffff20', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  chatWindow: { backgroundColor: '#0d1628', borderRadius: '0 10px 10px 0', border: '1px solid #ffffff15', borderLeft: 'none', display: 'flex', flexDirection: 'column' },
  noRoom: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  noRoomText: { color: '#ffffff30', fontSize: '14px' },
  chatHeader: { padding: '14px 20px', borderBottom: '1px solid #ffffff10' },
  chatRoomName: { color: '#ffffff', fontSize: '15px', fontWeight: '700', margin: '0 0 2px 0' },
  chatRoomDesc: { color: '#ffffff50', fontSize: '12px', margin: '0' },
  messagesFeed: { flex: 1, padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
  emptyChat: { color: '#ffffff30', fontSize: '14px', textAlign: 'center', marginTop: '40px' },
  chatMessage: { display: 'flex', alignItems: 'flex-start', gap: '10px' },
  chatAvatar: { width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  chatMessageContent: { flex: 1 },
  chatMessageHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' },
  chatMessageAuthor: { color: '#C49A28', fontSize: '13px', fontWeight: '600' },
  chatMessageTime: { color: '#ffffff30', fontSize: '11px' },
  chatMessageBody: { color: '#ffffffcc', fontSize: '14px', margin: '0', lineHeight: '1.5' },
  deleteMsgBtn: { backgroundColor: 'transparent', border: 'none', color: '#ffffff20', fontSize: '16px', cursor: 'pointer', flexShrink: 0, padding: '0' },
  chatInputArea: { padding: '14px 20px', borderTop: '1px solid #ffffff10', display: 'flex', gap: '10px' },
  chatInput: { flex: 1, backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '14px', outline: 'none' },
  chatSendBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  forumLayout: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: '0', minHeight: '600px' },
  threadList: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '10px 0 0 10px', display: 'flex', flexDirection: 'column', overflowY: 'auto', maxHeight: '700px' },
  threadListHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid #ffffff10', flexShrink: 0 },
  threadListTitle: { color: '#ffffff', fontSize: '14px', fontWeight: '600', margin: '0' },
  newThreadBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  threadForm: { padding: '12px 16px', borderBottom: '1px solid #ffffff10', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 },
  threadItem: { padding: '14px 16px', borderBottom: '1px solid #ffffff08', cursor: 'pointer' },
  threadItemActive: { padding: '14px 16px', borderBottom: '1px solid #ffffff08', cursor: 'pointer', backgroundColor: '#1A2744' },
  threadItemTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' },
  threadItemTitle: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0', flex: 1 },
  threadItemActions: { display: 'flex', gap: '4px', flexShrink: 0 },
  threadItemMeta: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  pinIcon: { fontSize: '11px' },
  pinBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff60', borderRadius: '4px', padding: '2px 6px', fontSize: '10px', cursor: 'pointer' },
  deleteThreadBtn: { backgroundColor: 'transparent', border: 'none', color: '#ffffff30', fontSize: '14px', cursor: 'pointer', padding: '0 2px' },
  threadDetail: { backgroundColor: '#0d1628', border: '1px solid #ffffff15', borderLeft: 'none', borderRadius: '0 10px 10px 0', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  threadDetailHeader: { padding: '20px 24px', borderBottom: '1px solid #ffffff10', flexShrink: 0 },
  threadDetailTitle: { color: '#C49A28', fontSize: '18px', fontWeight: '700', margin: '0 0 6px 0' },
  threadDetailMeta: { color: '#ffffff40', fontSize: '12px', margin: '0 0 12px 0' },
  threadDetailBody: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.7', margin: '0' },
  repliesList: { flex: 1, padding: '16px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' },
  replyCard: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '14px' },
  replyHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
  replyAvatar: { width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  replyMeta: { flex: 1 },
  replyAuthor: { color: '#ffffff', fontSize: '13px', fontWeight: '600', margin: '0' },
  replyTime: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  deleteReplyBtn: { backgroundColor: 'transparent', border: 'none', color: '#ffffff20', fontSize: '16px', cursor: 'pointer', padding: '0' },
  replyBody: { color: '#ffffffcc', fontSize: '13px', lineHeight: '1.6', margin: '0' },
  replyInputArea: { padding: '16px 24px', borderTop: '1px solid #ffffff10', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 },
  replyInput: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box' },
  replySendBtn: { alignSelf: 'flex-end', backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  studyRoomContainer: { display: 'flex', justifyContent: 'center', paddingTop: '20px' },
  studyRoomCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff15', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '600px' },
  studyRoomHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' },
  studyRoomTitle: { color: '#C49A28', fontSize: '22px', fontWeight: '700', margin: '0 0 6px 0' },
  studyRoomSubtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  editStudyBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '6px', padding: '6px 16px', fontSize: '13px', cursor: 'pointer' },
  studyEditForm: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' },
  liveToggle: { display: 'flex', alignItems: 'center' },
  toggleLabel: { display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' },
  toggleInput: { width: '18px', height: '18px', cursor: 'pointer' },
  toggleText: { color: '#ffffff', fontSize: '14px', fontWeight: '600' },
  studyRoomBody: { display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', padding: '20px 0' },
  liveStatus: { display: 'flex', alignItems: 'center', gap: '10px' },
  liveDot: { width: '12px', height: '12px', borderRadius: '50%' },
  liveText: { fontSize: '16px', fontWeight: '700' },
  studyMessage: { backgroundColor: '#1A2744', borderRadius: '10px', padding: '16px 20px', width: '100%', boxSizing: 'border-box' },
  studyMessageText: { color: '#ffffffcc', fontSize: '14px', lineHeight: '1.7', margin: '0', textAlign: 'center' },
  joinBtn: { backgroundColor: '#1E8449', color: '#ffffff', borderRadius: '10px', padding: '14px 32px', fontSize: '16px', fontWeight: '700', textDecoration: 'none', display: 'inline-block' },
  noSessionText: { color: '#ffffff40', fontSize: '14px', textAlign: 'center' },
  input: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '10px 14px', color: '#ffffff', fontSize: '13px', outline: 'none', resize: 'vertical', width: '100%', boxSizing: 'border-box' },
  formActions: { display: 'flex', gap: '8px' },
  saveBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '8px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '6px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' },
  emptyText: { color: '#ffffff30', fontSize: '13px', padding: '16px', margin: '0' },
  loadingText: { color: '#ffffff60', fontSize: '13px', padding: '16px' },
  successText: { color: '#1E8449', fontSize: '13px', margin: '0 0 16px 0' },
};

export default Lounge;